/* eslint-disable react-refresh/only-export-components */
/**
 * Profile Context
 *
 * Provides shared customer profile state across the app.
 * Auto-fetches and decrypts profile when encryption layer is unlocked.
 *
 * This enables components like Dashboard to display the customer's name
 * without each page independently fetching the profile.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'
import { useEncryption } from './EncryptionContext'
import { useAuth } from '@/hooks/useAuth'
import { profileService } from '@/services/profile.service'
import {
  transformDecryptedProfile,
  type DecryptedCustomerData,
} from '@/utils/profileTransform'
import type { CustomerProfile, CommunicationPreferences } from '@/types/profile'

// ============================================================================
// TYPES
// ============================================================================

interface ProfileContextType {
  /** Customer profile (decrypted) */
  profile: CustomerProfile | null

  /** Whether profile is currently loading */
  isLoading: boolean

  /** Error message if profile fetch failed */
  error: string | null

  /** Refresh profile from API (e.g., after preference update) */
  refreshProfile: () => Promise<void>

  /** Update local profile preferences (after successful API save) */
  updateProfilePreferences: (preferences: CommunicationPreferences) => void
}

// ============================================================================
// CONTEXT
// ============================================================================

const ProfileContext = createContext<ProfileContextType | undefined>(undefined)

interface ProfileProviderProps {
  children: ReactNode
}

export function ProfileProvider({ children }: ProfileProviderProps) {
  const { isUnlocked, decryptRecord } = useEncryption()
  const { user } = useAuth()

  const [profile, setProfile] = useState<CustomerProfile | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hasAttemptedRef = useRef(false)

  /**
   * Fetch and decrypt profile from API
   */
  const loadProfile = useCallback(async () => {
    if (!isUnlocked) return

    hasAttemptedRef.current = true
    setIsLoading(true)
    setError(null)

    try {
      const result = await profileService.fetchProfile()

      if (!result) {
        setError('Profile not found')
        setProfile(null)
        return
      }

      const { encryptedRecord, apiResponse } = result

      // Decrypt the encrypted customer data
      const decryptedData =
        await decryptRecord<DecryptedCustomerData>(encryptedRecord)

      // Transform flat structure to CustomerProfile format
      // consultantName and consultantWebsite come from Cognito custom attributes (set during registration)
      const transformedProfile = transformDecryptedProfile(
        decryptedData,
        apiResponse,
        user?.consultantName,
        user?.consultantWebsite,
        user?.consultantIndustry
      )

      setProfile(transformedProfile)
    } catch (err) {
      console.error('Failed to load profile:', err)
      setError(err instanceof Error ? err.message : 'Failed to load profile')
      setProfile(null)
    } finally {
      setIsLoading(false)
    }
  }, [isUnlocked, decryptRecord, user?.consultantName, user?.consultantWebsite, user?.consultantIndustry])

  /**
   * Auto-fetch profile when encryption becomes unlocked
   */
  useEffect(() => {
    if (isUnlocked && !profile && !isLoading && !hasAttemptedRef.current) {
      loadProfile()
    }
  }, [isUnlocked, profile, isLoading, loadProfile])

  /**
   * Clear profile when encryption is locked
   */
  useEffect(() => {
    if (!isUnlocked) {
      setProfile(null)
      setError(null)
      hasAttemptedRef.current = false
    }
  }, [isUnlocked])

  /**
   * Sync consultant name when it becomes available after profile load
   * This handles the race condition where profile loads before auth completes
   */
  useEffect(() => {
    if (
      profile &&
      user?.consultantName &&
      profile.consultantName !== user.consultantName
    ) {
      setProfile({ ...profile, consultantName: user.consultantName })
    }
  }, [profile, user?.consultantName])

  useEffect(() => {
    if (
      profile &&
      user?.consultantIndustry &&
      profile.consultantIndustry !== user.consultantIndustry
    ) {
      setProfile({ ...profile, consultantIndustry: user.consultantIndustry })
    }
  }, [profile, user?.consultantIndustry])

  /**
   * Refresh profile from API
   */
  const refreshProfile = useCallback(async () => {
    hasAttemptedRef.current = false
    await loadProfile()
  }, [loadProfile])

  /**
   * Update local profile preferences (after successful API save)
   * Avoids need to re-fetch entire profile just for preference changes
   */
  const updateProfilePreferences = useCallback(
    (preferences: CommunicationPreferences) => {
      if (profile) {
        setProfile({ ...profile, preferences })
      }
    },
    [profile]
  )

  // Memoize context value
  const value = useMemo<ProfileContextType>(
    () => ({
      profile,
      isLoading,
      error,
      refreshProfile,
      updateProfilePreferences,
    }),
    [profile, isLoading, error, refreshProfile, updateProfilePreferences]
  )

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  )
}

/**
 * Hook to access profile context
 */
export function useProfile(): ProfileContextType {
  const context = useContext(ProfileContext)
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider')
  }
  return context
}

export default ProfileContext
